/**
 * Open an https URL from a Telegram Mini App or a normal browser.
 *
 * Mobile WebViews block `window.open` after an `await` (payment POST), so we prefer
 * `Telegram.WebApp.openLink` with Instant View disabled — PayHot/NOWPayments checkout
 * pages often fail silently when Instant View tries to render them in-app.
 *
 * @param {string} url
 * @param {{ webApp?: { openLink?: Function } | null }} [options]
 * @returns {boolean} true when an open strategy was invoked without throwing
 */
export function openExternalUrl(url, options = {}) {
  if (typeof url !== "string" || !url.trim()) {
    return false;
  }

  const href = url.trim();
  const webApp =
    options.webApp !== undefined
      ? options.webApp
      : typeof window !== "undefined"
        ? window.Telegram?.WebApp
        : null;

  if (typeof webApp?.openLink === "function") {
    try {
      webApp.openLink(href, { try_instant_view: false });
      return true;
    } catch {
      try {
        webApp.openLink(href);
        return true;
      } catch {
        // Fall through to window.open / location.assign.
      }
    }
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    // Do not pass `noopener` in windowFeatures: Safari returns null even when the
    // tab opened, and the location.assign fallback below would then navigate this tab too.
    const popup = window.open(href, "_blank");
    if (popup) {
      try {
        popup.opener = null;
      } catch {
        // Cross-origin proxies may reject the write; the tab is already open.
      }
      return true;
    }
  } catch {
    // Popup APIs can throw in restricted WebViews.
  }

  try {
    window.location.assign(href);
    return true;
  } catch {
    return false;
  }
}
